/**
 * Product USP Bar - Tooltip Functionality
 * Handles tooltip show/hide interactions for USP bars
 * No animations - instant show/hide for better UX
 */

class USPBar {
  constructor() {
    this.activeTooltip = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.bindEvents());
    } else {
      this.bindEvents();
    }
  }

  bindEvents() {
    const uspBars = document.querySelectorAll('[data-usp-bar]');

    uspBars.forEach((bar) => {
      // Info button click handlers
      const triggers = bar.querySelectorAll('[data-usp-trigger]');
      triggers.forEach((trigger) => {
        const uspId = trigger.dataset.uspTrigger;
        const tooltip = bar.querySelector(`[data-usp-tooltip="${uspId}"]`);

        if (!tooltip) return;

        // Desktop: Hover works via CSS (:hover pseudo-class)
        // No JavaScript needed for hover!

        // Mobile: Touch events for toggling
        trigger.addEventListener('touchstart', (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          // Toggle tooltip on touch
          this.toggleTooltip(tooltip, trigger);
        }, { passive: false, capture: true });

        // Prevent click event from opening modal
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }, true);

        // Keyboard accessibility
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggleTooltip(tooltip, trigger);
          }
          if (e.key === 'Escape') {
            this.hideTooltip(tooltip, trigger);
          }
        });
      });
    });

    // Close tooltips when clicking outside
    document.addEventListener('click', (e) => {
      if (
        this.activeTooltip &&
        !this.activeTooltip.contains(e.target) &&
        !e.target.closest('[data-usp-trigger]')
      ) {
        const trigger = document.querySelector(
          `[data-usp-trigger="${this.activeTooltip.dataset.uspTooltip}"]`
        );
        this.hideTooltip(this.activeTooltip, trigger);
      }
    });

    // Close tooltips on scroll (mobile UX)
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      if (this.activeTooltip) {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          const trigger = document.querySelector(
            `[data-usp-trigger="${this.activeTooltip.dataset.uspTooltip}"]`
          );
          this.hideTooltip(this.activeTooltip, trigger);
        }, 100);
      }
    });
  }

  showTooltip(tooltip, trigger) {
    // Hide any other active tooltip first
    if (this.activeTooltip && this.activeTooltip !== tooltip) {
      const oldTrigger = document.querySelector(
        `[data-usp-trigger="${this.activeTooltip.dataset.uspTooltip}"]`
      );
      this.hideTooltip(this.activeTooltip, oldTrigger);
    }

    // Show tooltip instantly (no animation) - use active class
    tooltip.classList.add('active');
    tooltip.setAttribute('aria-hidden', 'false');

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    this.activeTooltip = tooltip;

    // Adjust positioning if tooltip goes off-screen
    this.adjustTooltipPosition(tooltip);
  }

  hideTooltip(tooltip, trigger) {
    // Hide instantly (no animation) - remove active class
    tooltip.classList.remove('active');
    tooltip.setAttribute('aria-hidden', 'true');

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    if (this.activeTooltip === tooltip) {
      this.activeTooltip = null;
    }
  }

  toggleTooltip(tooltip, trigger) {
    const isHidden = !tooltip.classList.contains('active');

    if (isHidden) {
      this.showTooltip(tooltip, trigger);
    } else {
      this.hideTooltip(tooltip, trigger);
    }
  }

  adjustTooltipPosition(tooltip) {
    // Check if tooltip goes off-screen and adjust if needed
    const rect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // Reset transform first
    tooltip.style.transform = '';

    // Check if tooltip overflows left
    if (rect.left < 10) {
      tooltip.style.left = '10px';
      tooltip.style.transform = 'none';
    }
    // Check if tooltip overflows right
    else if (rect.right > viewportWidth - 10) {
      tooltip.style.left = 'auto';
      tooltip.style.right = '10px';
      tooltip.style.transform = 'none';
    }
    // Default: centered
    else {
      tooltip.style.left = '50%';
      tooltip.style.right = 'auto';
      tooltip.style.transform = 'translateX(-50%)';
    }
  }
}

// Initialize USP Bar functionality
if (typeof window.uspBar === 'undefined') {
  window.uspBar = new USPBar();
}
